import * as THREE from 'three';
import type { V2 } from '../../core/math';
import type { PackModel } from '../../render/models/modelLibrary';
import { emitStreetModel } from '../../render/models/streetModels';
import type { Decor, WallDecor } from '../decor';
import { pointInPolygon } from '../geom';
import { BUS_STREETS, districtAt } from '../lowBorough/data';
import type { BuildingLot, CityLayout } from '../types';
import { PAVE_Y, WATER_Y } from './ground';
import { ChunkedBuilders, hash01, MeshBuilder, type RGB } from './meshBuilder';
import { BAKED_GROUND, KERB_FACE } from './textures';
import { T, tileUV, type SignAtlas } from './textures';

/**
 * Street life (city pass 2): the small things that make Low Borough read as lived-in from the high
 * camera — bus stops, dustbins, bikes and prams, back-yard washing lines, fly-posters and graffiti,
 * weeds, gutter grime, manholes and tar patches, shop-light pools on the pavement, puddles, and a
 * few lighters moored in the canal and basin.
 *
 * VISUAL ONLY. Nothing here adds a collider or touches the CityLayout: the online server validates
 * movement against the layout's colliders, so these must never block anything. Everything is merged
 * into the existing chunk builders (no new materials -> no new draw calls), except the puddles, which
 * are one small mesh whose opacity follows the weather (see `createPuddles`).
 */

const WHITE = tileUV(T.white, 40);
const GALV: RGB = [0.42, 0.43, 0.42];

/** Fictional period fly-posters ("HEADLINE · line · line"). */
const POSTERS = [
  'WRESTLING · Kessmouth Baths · Saturday 7.30',
  'DANCING · Gullet Palais · Every Friday',
  'THE KESTRELS · Live at the Anchor · Thursday',
  'BOXING · Brannoc A.B.C. · Ten Bouts',
  'CIRCUS · Ropewalk Field · One Week Only',
  "JUMBLE SALE · St. Brannoc's Hall · Sat 2 p.m.",
  'DOG TRACK · Cinder Stadium · Tues & Sat',
  'GRAND BINGO · Electric Palace · Nightly',
  'DOCKERS MEETING · Labour Exchange · Monday 7',
  'SOUL NIGHT · The Velvet Lamp · Fridays',
  'MISSING · Have You Seen This Dog? · Ask at Hobbs',
  'ROOMS TO LET · Clean · Apply Within',
];
/** Spray-paint tags. Fictional and period, no real slogans or names. */
const TAGS = ['LOW BORO', 'GULLET MOB', 'MODS', 'SKA', 'KILROY', 'TEDS RULE', 'DOCKERS', 'STAN IS A GRASS', 'KESSMOUTH UTD', 'WE WANT WORK', 'BOVVER', 'ARCH BOYS'];
const LITTER: RGB[] = [
  [0.62, 0.6, 0.55],
  [0.5, 0.48, 0.42],
  [0.32, 0.2, 0.08],
  [0.42, 0.28, 0.1],
  [0.6, 0.12, 0.08],
  [0.75, 0.72, 0.62],
];
const ROUTES = ['14', '27', '38', '9 · 14', '27 · 38', '62'];

type P3 = [number, number, number];

/**
 * The street models this pass draws in place of its boxes and beams (MEDIUM and HIGH; each null when the pack
 * has none): the Meshy bus shelter and the Blender street-life kit (art/models/blender/export_street_life.py).
 * Clutter (`clutter`) is never drawn on LOW, so a bike or pram model simply replaces its beams; what sits in
 * the all-tier `props` (the bus-stop post, the boats, the washing poles) keeps its procedural self in
 * `propsLow` as the LOW stand-in, as the Meshy families do.
 */
export interface StreetLifeKit {
  shelter: PackModel | null;
  bicycle: PackModel | null;
  pram: PackModel | null;
  busStop: PackModel | null;
  lighter: PackModel | null;
  narrowboat: PackModel | null;
  milkCrate: PackModel | null;
  washingPole: PackModel | null;
}

const NO_KIT: StreetLifeKit = { shelter: null, bicycle: null, pram: null, busStop: null, lighter: null, narrowboat: null, milkCrate: null, washingPole: null };

interface Ctx {
  layout: CityLayout;
  decor: Decor;
  cb: ChunkedBuilders;
  signs: SignAtlas;
  lotGrid: Map<string, BuildingLot[]>;
  propGrid: Map<string, V2[]>;
  kit: StreetLifeKit;
}

const _mm = new THREE.Matrix4();
const _mq = new THREE.Quaternion();
const _ms = new THREE.Vector3();
const _mp = new THREE.Vector3();
const UP = new THREE.Vector3(0, 1, 0);

/** A model's placing matrix: at (x, y, z), turned by `yaw` (the Frame heading convention), scaled per axis. */
function place(x: number, y: number, z: number, yaw: number, sx = 1, sy = 1, sz = 1): THREE.Matrix4 {
  _mq.setFromAxisAngle(UP, yaw);
  return _mm.compose(_mp.set(x, y, z), _mq, _ms.set(sx, sy, sz));
}

/** The tint that turns a grey vertex-baked model (0.42 sRGB = 0.147 linear) into a paint. */
function paint(c: RGB, albedo = 0.147): { tint: RGB; untinted: RGB } {
  const t: RGB = [c[0] / albedo, c[1] / albedo, c[2] / albedo];
  return { tint: t, untinted: t };
}

/** The dairies' crate colours. */
const CRATE_PAINTS: RGB[] = [
  [0.45, 0.08, 0.06],
  [0.1, 0.18, 0.4],
  [0.55, 0.5, 0.38],
];

const CELL = 24;
const cellKey = (x: number, z: number): string => `${Math.floor(x / CELL)},${Math.floor(z / CELL)}`;

function lotLocal(lot: BuildingLot, p: V2): V2 {
  const dx = p.x - lot.center.x;
  const dz = p.z - lot.center.z;
  const c = Math.cos(lot.angle);
  const s = Math.sin(lot.angle);
  return { x: dx * c - dz * s, z: dx * s + dz * c };
}

function insideLot(c: Ctx, p: V2, pad = 0.3): boolean {
  const cx = Math.floor(p.x / CELL);
  const cz = Math.floor(p.z / CELL);
  for (let dx = -2; dx <= 2; dx++) {
    for (let dz = -2; dz <= 2; dz++) {
      for (const lot of c.lotGrid.get(`${cx + dx},${cz + dz}`) ?? []) {
        const l = lotLocal(lot, p);
        if (Math.abs(l.x) < lot.halfX + pad && Math.abs(l.z) < lot.halfZ + pad) return true;
      }
    }
  }
  return false;
}

function nearProp(c: Ctx, p: V2, r: number): boolean {
  const cx = Math.floor(p.x / CELL);
  const cz = Math.floor(p.z / CELL);
  for (let dx = -1; dx <= 1; dx++) {
    for (let dz = -1; dz <= 1; dz++) {
      for (const q of c.propGrid.get(`${cx + dx},${cz + dz}`) ?? []) if (Math.hypot(q.x - p.x, q.z - p.z) < r) return true;
    }
  }
  return false;
}

function onRoad(c: Ctx, p: V2, pad: number): boolean {
  const { nodes, edges } = c.layout.roads;
  for (const e of edges) {
    const a = nodes[e.from]!.pos;
    const b = nodes[e.to]!.pos;
    const dx = b.x - a.x;
    const dz = b.z - a.z;
    const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.z - a.z) * dz) / (dx * dx + dz * dz)));
    if (Math.hypot(p.x - a.x - dx * t, p.z - a.z - dz * t) < e.width / 2 + pad) return true;
  }
  for (const it of c.layout.intersections) if (Math.hypot(p.x - it.pos.x, p.z - it.pos.z) < it.radius + pad) return true;
  return false;
}

function inWater(c: Ctx, p: V2, pad = 0): boolean {
  return c.decor.water.some((w) => p.x > w.minX - pad && p.x < w.maxX + pad && p.z > w.minZ - pad && p.z < w.maxZ + pad);
}

/** Local frame (heading convention: +Z forward = (sin h, cos h), +X = (cos h, -sin h)). */
class Frame {
  readonly cs: number;
  readonly sn: number;
  constructor(
    readonly x: number,
    readonly z: number,
    readonly h: number,
    readonly y = PAVE_Y,
  ) {
    this.cs = Math.cos(h);
    this.sn = Math.sin(h);
  }
  p(lx: number, ly: number, lz: number): P3 {
    return [this.x + lx * this.cs + lz * this.sn, this.y + ly, this.z - lx * this.sn + lz * this.cs];
  }
  box(b: MeshBuilder, lx: number, ly: number, lz: number, sx: number, sy: number, sz: number, c: RGB, uv = WHITE): void {
    const p = this.p(lx, ly, lz);
    b.box(p[0], p[1], p[2], sx, sy, sz, this.h, c, uv);
  }
  cyl(b: MeshBuilder, lx: number, lz: number, y0: number, y1: number, r0: number, r1: number, segs: number, c: RGB, top = true): void {
    const p = this.p(lx, 0, lz);
    b.cylinder(p[0], p[2], this.y + y0, this.y + y1, r0, r1, segs, c, WHITE, { top });
  }
  /** Vertical wheel (ring of beams) in the local YZ plane at lx. */
  wheel(b: MeshBuilder, lx: number, lz: number, r: number, c: RGB, segs = 7): void {
    for (let i = 0; i < segs; i++) {
      const a0 = (i / segs) * Math.PI * 2;
      const a1 = ((i + 1) / segs) * Math.PI * 2;
      b.beam(this.p(lx, r + Math.sin(a0) * r, lz + Math.cos(a0) * r), this.p(lx, r + Math.sin(a1) * r, lz + Math.cos(a1) * r), 0.045, c, WHITE);
    }
  }
  /** A sign quad facing local +Z (or -Z when back = true), centred at lx, bottom at y. */
  sign(sb: MeshBuilder, uv: readonly [number, number, number, number], lx: number, y: number, lz: number, w: number, hgt: number, back = false): void {
    const s = back ? -1 : 1;
    sb.quad(this.p(lx - (s * w) / 2, y, lz), this.p(lx + (s * w) / 2, y, lz), this.p(lx + (s * w) / 2, y + hgt, lz), this.p(lx - (s * w) / 2, y + hgt, lz), uv, [1, 1, 1]);
  }
}

export interface StreetLifeStats {
  busStops: number;
  posters: number;
  tags: number;
  washingLines: number;
  bikes: number;
  weeds: number;
  manholes: number;
  puddles: number;
}

export function buildStreetLife(layout: CityLayout, decor: Decor, cb: ChunkedBuilders, signs: SignAtlas, kit: StreetLifeKit | null = null): { puddles: MeshBuilder; stats: StreetLifeStats } {
  const lotGrid = new Map<string, BuildingLot[]>();
  for (const lot of layout.buildings) {
    const k = cellKey(lot.center.x, lot.center.z);
    const list = lotGrid.get(k) ?? [];
    list.push(lot);
    lotGrid.set(k, list);
  }
  const propGrid = new Map<string, V2[]>();
  const addPropPt = (p: V2): void => {
    const k = cellKey(p.x, p.z);
    const list = propGrid.get(k) ?? [];
    list.push(p);
    propGrid.set(k, list);
  };
  for (const p of decor.props) addPropPt(p.pos);
  for (const p of decor.looseProps) addPropPt(p.pos);
  for (const s of decor.signalHeads) addPropPt(s.pos);
  for (const z of decor.zebras) addPropPt(z.pos);
  const c: Ctx = { layout, decor, cb, signs, lotGrid, propGrid, kit: kit ?? NO_KIT };
  const stats: StreetLifeStats = { busStops: 0, posters: 0, tags: 0, washingLines: 0, bikes: 0, weeds: 0, manholes: 0, puddles: 0 };
  const puddles = new MeshBuilder();

  roadWear(c, stats, puddles);
  busStops(c, stats, addPropPt);
  for (const lot of layout.buildings) frontage(c, lot, stats);
  backYards(c, stats);
  wallDressing(c, stats);
  shopLight(c);
  moored(c);
  return { puddles, stats };
}

// ------------------------------------------------------------------------------------- road wear

function roadWear(c: Ctx, stats: StreetLifeStats, puddles: MeshBuilder): void {
  const { nodes, edges } = c.layout.roads;
  const mk = c.cb.global('markings');
  const asphalt = c.cb.global('asphalt');
  const radiusAt = (nodeId: number): number => {
    const it = nodes[nodeId]!.intersectionId;
    return it !== null ? c.layout.intersections[it]!.radius : 0;
  };
  const Y = 0.017;
  // A gully grating in the gutter: a 0.7 × 0.32 m quad in the kerb mesh (both tiers' meshes, one is drawn) that
  // carries 2 + its uv into the kerb-face sheet, where the two cast-iron gratings are baked (materials.ts kerbUvMap);
  // on the canvas path a plain dark slab as before.
  const kerbs = [c.cb.global('kerb'), c.cb.global('kerbLite')];
  const grating = (cx: number, cz: number, d: V2, variant: number): void => {
    const [u0, v0, u1, v1] = KERB_FACE.grates[variant]!;
    const l = { x: d.z, z: -d.x };
    const a2 = 0.35;
    const c2 = 0.16;
    const P = (sa: number, sc: number): [number, number, number] => [cx + d.x * sa + l.x * sc, Y + 0.001, cz + d.z * sa + l.z * sc];
    const col: RGB = BAKED_GROUND ? [1, 1, 1] : [0.015, 0.015, 0.016];
    for (const b of kerbs) b.quadFacing(P(-a2, c2), P(a2, c2), P(a2, -c2), P(-a2, -c2), [2 + u0, 2 + v0, 2 + u1, 2 + v1], col, [0, 1, 0]);
  };
  const rect = (b: MeshBuilder, cx: number, cz: number, d: V2, along: number, across: number, y: number, col: RGB): void => {
    const l = { x: d.z, z: -d.x };
    const a2 = along / 2;
    const c2 = across / 2;
    b.polygon(
      [
        { x: cx - d.x * a2 - l.x * c2, z: cz - d.z * a2 - l.z * c2 },
        { x: cx + d.x * a2 - l.x * c2, z: cz + d.z * a2 - l.z * c2 },
        { x: cx + d.x * a2 + l.x * c2, z: cz + d.z * a2 + l.z * c2 },
        { x: cx - d.x * a2 + l.x * c2, z: cz - d.z * a2 + l.z * c2 },
      ],
      y,
      col,
    );
  };
  for (const e of edges) {
    const a = nodes[e.from]!.pos;
    const b = nodes[e.to]!.pos;
    const len = Math.hypot(b.x - a.x, b.z - a.z);
    if (len < 4) continue;
    const d = { x: (b.x - a.x) / len, z: (b.z - a.z) / len };
    const l = { x: d.z, z: -d.x };
    const t0 = radiusAt(e.from) + 1;
    const t1 = len - radiusAt(e.to) - 1;
    if (t1 - t0 < 3) continue;
    const bridge = c.decor.bridges.some((br) => br.edgeId === e.id);
    const setts = c.decor.setts[e.id] ?? false;
    const at = (t: number, s: number): V2 => ({ x: a.x + d.x * t + l.x * s, z: a.z + d.z * t + l.z * s });
    const half = e.width / 2;
    // Gutter grime: a dark band along both kerbs where silt, leaves and oil collect: the kerb sheet's gutter
    // strip (baked: silt at the foot thinning towards the lane, leaves, oil) on the baked path, a dark slab before.
    if (!e.alley && !bridge) {
      for (const side of [1, -1]) {
        if (BAKED_GROUND) {
          const k0 = at(t0, side * (half - 0.01));
          const k1 = at(t1, side * (half - 0.01));
          const r0 = at(t0, side * (half - 0.31));
          const r1 = at(t1, side * (half - 0.31));
          const P = (p: V2): [number, number, number] => [p.x, Y - 0.0015, p.z];
          // (u carries the distance along the kerb in sheet repeats, v 0 … 1 across: see materials.ts kerbUvMap)
          for (const b of kerbs) b.quadFacing(P(k0), P(k1), P(r1), P(r0), [4 + t0 / KERB_FACE.along, 4, 4 + t1 / KERB_FACE.along, 5], [1, 1, 1], [0, 1, 0]);
        } else {
          const g0 = at(t0, side * (half - 0.16));
          const g1 = at(t1, side * (half - 0.16));
          rect(mk, (g0.x + g1.x) / 2, (g0.z + g1.z) / 2, d, t1 - t0, 0.3, Y - 0.0015, [0.028, 0.027, 0.026]);
        }
        // litter washed into the gutter: fag packets, bus tickets, sodden leaves
        for (let t = t0 + 1; t < t1 - 1; t += 2.3) {
          const h = hash01(e.id * 2 + (side > 0 ? 1 : 0), Math.round(t * 10), 71);
          if (h > 0.45) continue;
          const p = at(t + h * 2, side * (half - 0.12 - h * 0.25));
          const lc = LITTER[Math.floor(h * 97) % LITTER.length]!;
          const ang = h * 9;
          rect(mk, p.x, p.z, { x: Math.cos(ang), z: Math.sin(ang) }, 0.22 + h * 0.2, 0.14 + h * 0.1, Y + 0.0012, lc);
        }
      }
    }
    if (setts) continue;
    // Wheel wear has soft shoulders and fades before the junction. Vertex colours blend back into
    // the same world-space asphalt texture; these strips stay in the existing ground batch.
    if (!bridge) {
      const lanes: number[] = [];
      for (let i = 0; i < e.lanesForward; i++) lanes.push(e.oneWay ? -half + e.laneWidth * (i + 0.5) : e.laneWidth * (i + 0.5));
      for (let i = 0; i < e.lanesBackward; i++) lanes.push(-e.laneWidth * (i + 0.5));
      for (const lc of lanes) {
        for (const off of [-0.8, 0.8]) {
          const fade = Math.min(3, (t1 - t0) / 3);
          const stops = [t0, t0 + fade, t1 - fade, t1];
          const widths = [-0.48, -0.16, 0.16, 0.48];
          const tone = 0.90 + hash01(e.id, Math.round((lc + off) * 10), 142) * 0.035;
          for (let ti = 0; ti < 3; ti++) {
            for (let wi = 0; wi < 3; wi++) {
              const base = asphalt.vertexCount;
              asphalt.polygon([
                at(stops[ti]!, lc + off + widths[wi]!),
                at(stops[ti + 1]!, lc + off + widths[wi]!),
                at(stops[ti + 1]!, lc + off + widths[wi + 1]!),
                at(stops[ti]!, lc + off + widths[wi + 1]!),
              ], 0.004, [1, 1, 1]);
              for (let v = 0; v < 4; v++) {
                const t = ti + (v === 1 || v === 2 ? 1 : 0);
                const w = wi + (v >= 2 ? 1 : 0);
                const shade = t === 0 || t === 3 || w === 0 || w === 3 ? 1 : tone;
                for (let ch = 0; ch < 3; ch++) asphalt.col[(base + v) * 3 + ch] = shade;
              }
            }
          }
        }
      }
    }
    // Tar patches: utility trenches and pothole repairs.
    for (let t = t0 + 4; t < t1 - 3; t += 9) {
      const h = hash01(e.id, Math.round(t), 41);
      if (h > 0.42) continue;
      const s = (hash01(e.id, Math.round(t), 43) - 0.5) * (e.width - 2.5);
      const p = at(t, s);
      const trench = h < 0.12;
      const along = trench ? 1.0 : 1.2 + hash01(e.id, Math.round(t), 44) * 2.2;
      const across = trench ? e.width * 0.3 : 0.9 + hash01(e.id, Math.round(t), 45) * 1.4;
      const tone = hash01(e.id, Math.round(t), 46) < 0.6 ? 0.8 : 1.12;
      rect(asphalt, p.x, p.z, d, along, across, 0.007, [tone, tone, tone * 1.02]);
    }
    // Manholes on the crown of the road, drain gratings in the gutters.
    for (let t = t0 + 6 + hash01(e.id, 3) * 10; t < t1 - 4; t += 26 + hash01(e.id, Math.round(t)) * 12) {
      const s = e.oneWay || e.alley ? 0 : (hash01(e.id, Math.round(t), 5) < 0.5 ? 1 : -1) * e.laneWidth * 0.5;
      const p = at(t, s);
      const pts: V2[] = [];
      for (let i = 0; i < 10; i++) pts.push({ x: p.x + Math.cos((i / 10) * Math.PI * 2) * 0.42, z: p.z + Math.sin((i / 10) * Math.PI * 2) * 0.42 });
      mk.polygon(pts, Y + 0.001, [0.045, 0.045, 0.048]);
      const inner = pts.map((q) => ({ x: p.x + (q.x - p.x) * 0.72, z: p.z + (q.z - p.z) * 0.72 }));
      mk.polygon(inner, Y + 0.002, [0.075, 0.072, 0.07]);
      stats.manholes++;
    }
    if (!e.alley && !bridge) {
      for (let t = t0 + 5; t < t1 - 2; t += 17) {
        for (const side of [1, -1]) {
          const p = at(t + side * 3, side * (half - 0.22));
          grating(p.x, p.z, d, hash01(e.id, Math.round(t), side + 7) < 0.7 ? 0 : 1);
        }
      }
    }
    // Puddles: in the gutters (where the camber drains) and in potholes on the narrow lanes.
    if (!bridge) {
      for (let t = t0 + 3; t < t1 - 3; t += 7) {
        const h = hash01(e.id, Math.round(t), 61);
        if (h > (e.alley ? 0.5 : 0.3)) continue;
        const side = hash01(e.id, Math.round(t), 62) < 0.5 ? 1 : -1;
        const s = e.alley ? (h - 0.25) * e.width : side * (half - 0.55 - hash01(e.id, Math.round(t), 63) * 0.8);
        const p = at(t, s);
        const r = 0.7 + hash01(e.id, Math.round(t), 64) * 1.3;
        const ang = hash01(e.id, Math.round(t), 65) * Math.PI;
        const ca = Math.cos(ang);
        const sa = Math.sin(ang);
        const rx = r * 1.7;
        const rz = r;
        const Q = (u: number, v: number): P3 => [p.x + u * rx * ca - v * rz * sa, 0.009, p.z + u * rx * sa + v * rz * ca];
        puddles.quad(Q(-1, 1), Q(1, 1), Q(1, -1), Q(-1, -1), [0, 0, 1, 1], [1, 1, 1]);
        stats.puddles++;
      }
    }
  }
  // A few puddles on the cinder yards and the dock apron.
  for (let i = 0; i < 160; i++) {
    const x = -290 + hash01(i, 1, 91) * 580;
    const z = -290 + hash01(i, 2, 91) * 580;
    const p = { x, z };
    // Only on the unmade ground of the yards and the apron: never on lawns, park paths or pavements.
    const d = districtAt(p);
    if (d !== 'cinder-yards' && d !== 'gullet-basin') continue;
    if (inWater(c, p, 2) || insideLot(c, p, 0.6) || onRoad(c, p, 3.5)) continue;
    if (c.decor.grass.some((g) => pointInPolygon(p, g))) continue;
    const r = 0.8 + hash01(i, 3, 91) * 1.6;
    const y = PAVE_Y + 0.012;
    puddles.quad([x - r * 1.5, y, z + r], [x + r * 1.5, y, z + r], [x + r * 1.5, y, z - r], [x - r * 1.5, y, z - r], [0, 0, 1, 1], [1, 1, 1]);
    stats.puddles++;
  }
}

// ------------------------------------------------------------------------------------- bus stops

function busStops(c: Ctx, stats: StreetLifeStats, claim: (p: V2) => void): void {
  const { nodes, edges } = c.layout.roads;
  const sb = c.cb.global('signs');
  let k = 0;
  for (const e of edges) {
    if (!BUS_STREETS.has(e.name) || e.alley || e.sidewalk < 1.5) continue;
    if (c.decor.bridges.some((br) => br.edgeId === e.id)) continue;
    const a = nodes[e.from]!.pos;
    const b = nodes[e.to]!.pos;
    const len = Math.hypot(b.x - a.x, b.z - a.z);
    // the wide pavements (The Parade, Kiln Street) always get their stops: they are the ones with a shelter
    if (len < 45 || (hash01(e.id, 7, 13) < 0.35 && e.sidewalk < 3)) continue;
    const d = { x: (b.x - a.x) / len, z: (b.z - a.z) / len };
    const l = { x: d.z, z: -d.x };
    for (const [tf, side] of [
      [0.32, 1],
      [0.7, -1],
    ] as const) {
      // traffic on the LEFT: the stop for traffic travelling a->b is on the left kerb (side +1)
      if (side === 1 && e.lanesForward === 0) continue;
      if (side === -1 && e.lanesBackward === 0) continue;
      let placed = false;
      for (let tries = 0; tries < 6 && !placed; tries++) {
        const t = len * tf + (tries % 2 ? 1 : -1) * Math.ceil(tries / 2) * 3.5;
        const off = e.width / 2 + 0.55;
        const p = { x: a.x + d.x * t + l.x * side * off, z: a.z + d.z * t + l.z * side * off };
        if (insideLot(c, p, 0.4) || nearProp(c, p, 2.4) || inWater(c, p, 3)) continue;
        // pole faces along the road; local +Z points into the road
        const heading = Math.atan2(-l.x * side, -l.z * side);
        const F = new Frame(p.x, p.z, heading);
        // the post: the street-life model (its flag plate over the kerb, the timetable case on the pavement side;
        // the sign quads below sit 5 mm in front of its faces), the plain pole as its LOW stand-in
        const post = c.kit.busStop;
        if (post) emitStreetModel(c.cb.get('propsModel', p.x, p.z), post, place(p.x, PAVE_Y, p.z, heading), { foot: 0.72, footHeight: 0.3 });
        const props = c.cb.get(post ? 'propsLow' : 'props', p.x, p.z);
        F.cyl(props, 0, 0, 0, 2.75, 0.05, 0.045, 6, [0.12, 0.14, 0.13], false);
        const flag = c.signs.add(ROUTES[k % ROUTES.length]!, 'busStop');
        // flag is perpendicular to the kerb so it reads from up and down the street
        const G = new Frame(p.x, p.z, heading + Math.PI / 2);
        G.sign(sb, flag.uv, -0.3, 2.15, 0.02, 0.55, 0.55 / flag.aspect);
        G.sign(sb, flag.uv, -0.3, 2.15, -0.02, 0.55, 0.55 / flag.aspect, true);
        const tt = c.signs.add(`TT${k % 4}`, 'timetable');
        F.sign(sb, tt.uv, 0, 1.25, -0.06, 0.32, 0.32 / tt.aspect, true);
        // a shelter on the wide pavements: glazed back and ends, flat roof, advert panel
        if (e.sidewalk >= 3) {
          const sz = -Math.min(e.sidewalk - 1.6, 1.8);
          // with the Meshy shelter (MEDIUM / HIGH) the procedural frame and glass become its LOW stand-in
          const low = c.kit.shelter ? 'Low' : '';
          const sp = c.cb.get(`props${low}`, p.x, p.z);
          const fb = c.cb.get(`facade${low}`, p.x, p.z);
          const frame: RGB = [0.16, 0.2, 0.18];
          for (const sx of [-1.5, 1.5]) for (const lz of [sz - 0.5, sz + 0.5]) F.box(sp, sx, 0, lz, 0.07, 2.3, 0.07, frame);
          F.box(sp, 0, 2.3, sz, 3.3, 0.1, 1.3, [0.2, 0.22, 0.21]);
          F.box(fb, 0, 0.35, sz - 0.5, 2.9, 1.8, 0.04, [0.8, 0.85, 0.9], tileUV(T.glazingDark));
          for (const sx of [-1.5, 1.5]) F.box(fb, sx, 0.35, sz, 0.04, 1.8, 0.9, [0.8, 0.85, 0.9], tileUV(T.glazingDark));
          if (c.kit.shelter) {
            const at = F.p(0, 0, sz);
            emitStreetModel(c.cb.get('propsModel', p.x, p.z), c.kit.shelter, place(at[0], at[1], at[2], heading), { foot: 0.8, footHeight: 0.3 });
          }
          F.box(c.cb.get('props', p.x, p.z), 0, 0.45, sz - 0.3, 2.0, 0.05, 0.3, [0.32, 0.22, 0.14]);
          const ad = c.signs.add(POSTERS[(k + 3) % POSTERS.length]!, 'poster');
          F.sign(sb, ad.uv, 1.05, 0.7, sz - 0.47, 0.75, 0.75 / ad.aspect);
        }
        claim(p);
        stats.busStops++;
        k++;
        placed = true;
      }
    }
  }
}

// ------------------------------------------------------------------------------------- frontage

/**
 * Builder for tier-thinned clutter. cityView appends it to the end of the same chunk's `props`
 * geometry (no extra draw calls) and trims it off with the draw range on lower quality tiers.
 */
function detail(c: Ctx, key: 'clutter' | 'clutterFine', x: number, z: number): MeshBuilder {
  return c.cb.get(key, x, z);
}

function dustbin(b: MeshBuilder, F: Frame, lx: number, lz: number, lidOff: boolean): void {
  F.cyl(b, lx, lz, 0, 0.7, 0.26, 0.29, 8, GALV, false);
  for (const y of [0.22, 0.5]) F.cyl(b, lx, lz, y, y + 0.03, 0.3, 0.3, 8, [0.32, 0.33, 0.32], false);
  if (lidOff) F.cyl(b, lx + 0.45, lz + 0.1, 0, 0.05, 0.3, 0.3, 8, GALV);
  else F.cyl(b, lx, lz, 0.7, 0.8, 0.31, 0.12, 8, GALV);
}

function bicycle(c: Ctx, b: MeshBuilder, F: Frame, lx: number, lz: number, col: RGB): void {
  // leaning on the wall, side-on: frame along local X (wheels in the local XY plane via a rotated frame)
  const G = new Frame(F.p(lx, 0, lz)[0], F.p(lx, 0, lz)[2], F.h + Math.PI / 2, F.y);
  if (c.kit.bicycle) {
    // the roadster model: its length along G's z, leaning toward G's +x (the house); black enamel, no tint
    emitStreetModel(b, c.kit.bicycle, place(G.x, G.y, G.z, G.h), { foot: 0.8, footHeight: 0.2 });
    return;
  }
  G.wheel(b, 0, -0.52, 0.33, [0.05, 0.05, 0.05]);
  G.wheel(b, 0, 0.52, 0.33, [0.05, 0.05, 0.05]);
  b.beam(G.p(0, 0.33, -0.52), G.p(0, 0.75, -0.05), 0.05, col, WHITE);
  b.beam(G.p(0, 0.33, -0.52), G.p(0, 0.38, 0.05), 0.05, col, WHITE);
  b.beam(G.p(0, 0.38, 0.05), G.p(0, 0.85, 0.3), 0.05, col, WHITE);
  b.beam(G.p(0, 0.75, -0.05), G.p(0, 0.85, 0.3), 0.05, col, WHITE);
  b.beam(G.p(0, 0.85, 0.3), G.p(0, 0.33, 0.52), 0.05, col, WHITE);
  b.beam(G.p(0, 0.75, -0.05), G.p(0, 0.9, -0.12), 0.04, col, WHITE);
  G.box(b, 0, 0.9, -0.14, 0.12, 0.05, 0.24, [0.08, 0.06, 0.05]);
  b.beam(G.p(-0.24, 1.0, 0.36), G.p(0.24, 1.0, 0.36), 0.04, [0.6, 0.6, 0.58], WHITE);
}

function pram(c: Ctx, b: MeshBuilder, F: Frame, lx: number, lz: number): void {
  const G = new Frame(F.p(lx, 0, lz)[0], F.p(lx, 0, lz)[2], F.h + Math.PI / 2, F.y);
  if (c.kit.pram) {
    emitStreetModel(b, c.kit.pram, place(G.x, G.y, G.z, G.h), { foot: 0.8, footHeight: 0.25 });
    return;
  }
  for (const sx of [-0.24, 0.24]) {
    G.wheel(b, sx, -0.32, 0.2, [0.06, 0.06, 0.06], 6);
    G.wheel(b, sx, 0.32, 0.2, [0.06, 0.06, 0.06], 6);
  }
  const body: RGB = [0.1, 0.12, 0.22];
  G.box(b, 0, 0.42, 0, 0.5, 0.36, 0.9, body);
  G.box(b, 0, 0.78, -0.3, 0.52, 0.32, 0.32, [0.08, 0.08, 0.1]);
  b.beam(G.p(-0.22, 0.75, 0.42), G.p(-0.22, 0.98, 0.7), 0.03, [0.7, 0.7, 0.68], WHITE);
  b.beam(G.p(0.22, 0.75, 0.42), G.p(0.22, 0.98, 0.7), 0.03, [0.7, 0.7, 0.68], WHITE);
  b.beam(G.p(-0.24, 0.98, 0.7), G.p(0.24, 0.98, 0.7), 0.035, [0.15, 0.12, 0.1], WHITE);
}

const BIKE_COLOURS: RGB[] = [
  [0.12, 0.25, 0.4],
  [0.5, 0.1, 0.08],
  [0.1, 0.1, 0.1],
  [0.2, 0.32, 0.2],
];

function frontage(c: Ctx, lot: BuildingLot, stats: StreetLifeStats): void {
  const W = lot.halfX * 2;
  const D = lot.halfZ * 2;
  const F = new Frame(lot.center.x, lot.center.z, lot.angle);
  // Loose clutter goes into its own chunks so the quality tier can thin it (cityView: 'clutter' is
  // drawn on HIGH and MEDIUM, 'clutterFine' — weeds, milk bottles — on HIGH only).
  const props = detail(c, 'clutter', lot.center.x, lot.center.z);
  const fine = detail(c, 'clutterFine', lot.center.x, lot.center.z);
  const id = lot.id;
  const free = (lx: number, lz: number, r = 0.9): boolean => {
    const p = F.p(lx, 0, lz);
    const w = { x: p[0], z: p[2] };
    return !nearProp(c, w, r) && !inWater(c, w, 1);
  };
  if (lot.style === 'terrace') {
    const units = Math.max(1, Number(lot.tags.find((t) => t.startsWith('units:'))?.slice(6) ?? Math.round(W / 5.2)));
    const unitW = W / units;
    const garden = lot.tags.includes('garden');
    for (let u = 0; u < units; u++) {
      const x0 = -W / 2 + u * unitW;
      const doorLeft = hash01(id, u, 7) >= 0.5 ? false : true; // mirrors buildings.ts `flipped`
      const doorX = x0 + (doorLeft ? unitW * 0.75 : unitW * 0.25);
      const bayX = x0 + (doorLeft ? unitW * 0.25 : unitW * 0.75);
      void bayX;
      const r = hash01(id, u, 301);
      const bx = doorX + (doorX > x0 + unitW / 2 ? 0.8 : -0.8);
      const z = D / 2 + (garden ? 0.45 : 0.35);
      if (r < 0.45 && free(bx, z)) dustbin(props, F, bx, z, r < 0.08);
      else if (r < 0.52 && free(bx, z)) {
        bicycle(c, props, F, bx, D / 2 + 0.22, BIKE_COLOURS[u % BIKE_COLOURS.length]!);
        stats.bikes++;
      } else if (r < 0.56 && free(bx, z)) pram(c, props, F, bx, D / 2 + 0.6);
      // milk bottles on the step
      if (hash01(id, u, 303) < 0.35) {
        for (let m = 0; m < 2; m++) F.cyl(fine, doorX - 0.15 + m * 0.12, D / 2 + 0.35, 0.14, 0.36, 0.045, 0.04, 5, [0.92, 0.92, 0.9]);
      }
      // weeds in the crack between the flags and the wall (or the garden wall)
      if (hash01(id, u, 304) < 0.3) {
        const wz = garden ? D / 2 + 0.98 : D / 2 + 0.1;
        weed(fine, F.p(x0 + unitW * (0.15 + hash01(id, u, 305) * 0.7), 0, wz), hash01(id, u, 306) * 0.75);
        stats.weeds++;
      }
    }
    return;
  }
  if (lot.style === 'shopfront' || lot.style === 'cafe' || lot.style === 'pub') {
    // dropped wrappers, a crushed packet and old chewing gum on the flags outside the door
    const mk = c.cb.global('markings');
    const n = 1 + Math.floor(hash01(id, 312) * 3);
    for (let i = 0; i < n; i++) {
      const lx = (hash01(id, i, 313) - 0.5) * (W - 1);
      const lz = D / 2 + 0.4 + hash01(id, i, 314) * 2.4;
      const p = F.p(lx, 0.006, lz);
      const lc = LITTER[i % LITTER.length]!;
      const a = hash01(id, i, 315) * 6.28;
      const sx = 0.12 + hash01(id, i, 316) * 0.14;
      const ca = Math.cos(a) * sx;
      const sa = Math.sin(a) * sx;
      mk.quadFacing([p[0] - ca + sa * 0.7, p[1], p[2] - sa - ca * 0.7], [p[0] + ca + sa * 0.7, p[1], p[2] + sa - ca * 0.7], [p[0] + ca - sa * 0.7, p[1], p[2] + sa + ca * 0.7], [p[0] - ca - sa * 0.7, p[1], p[2] - sa + ca * 0.7], WHITE, lc, [0, 1, 0]);
    }
    // a bike against the pilaster, crates by the side door
    const r = hash01(id, 311);
    const z = D / 2 + 0.25;
    if (r < 0.22 && free(-W / 2 + 0.9, z)) {
      bicycle(c, props, F, -W / 2 + 0.9, z, [0.1, 0.1, 0.1]);
      stats.bikes++;
    }
    if (c.kit.milkCrate && r > 0.55 && free(W / 2 - 0.7, z, 0.6)) {
      // a dairy's crates stacked by the other pilaster, the top one askew (the pub's are beer crates, the same)
      const n = 2 + Math.floor(hash01(id, 320) * 2);
      const col = paint(CRATE_PAINTS[id % CRATE_PAINTS.length]!, 0.133);
      for (let i = 0; i < n; i++) {
        const p = F.p(W / 2 - 0.7, 0, z + 0.05);
        emitStreetModel(props, c.kit.milkCrate, place(p[0], PAVE_Y + i * 0.27, p[2], F.h + (i === n - 1 ? 0.25 : 0)), { ...col, foot: i ? 1 : 0.75, footHeight: 0.1 });
      }
    }
    if (lot.style === 'pub') {
      // beer crates and a stack of barrels by the cellar flap
      const cx = W / 2 - 1.6;
      F.box(props, cx, 0, D / 2 + 0.6, 1.4, 0.04, 0.9, [0.16, 0.12, 0.08]);
      for (let i = 0; i < 2; i++) F.cyl(props, W / 2 - 0.6, D / 2 + 0.45 + i * 0.62, 0, 0.62, 0.27, 0.27, 8, [0.38, 0.25, 0.14]);
    } else if (hash01(id, 317) < 0.2) {
      // a weed at the foot of a pilaster
      weed(fine, F.p((hash01(id, 318) < 0.5 ? -1 : 1) * (W / 2 - 0.5), 0, D / 2 + 0.08), hash01(id, 319) * 0.6);
      stats.weeds++;
    }
    return;
  }
  if (lot.style === 'warehouse' || lot.style === 'factory' || lot.style === 'shed' || lot.style === 'lockup' || lot.style === 'garage') {
    // pallets and oil drums against the front, weeds at the wall foot
    const r = hash01(id, 321);
    if (r < 0.5 && free(W / 2 - 1.5, D / 2 + 0.6, 1.2)) {
      for (let i = 0; i < 3; i++) F.box(props, W / 2 - 1.5, i * 0.15, D / 2 + 0.7, 1.2, 0.13, 1.0, [0.42, 0.32, 0.2], tileUV(T.timber));
    }
    if (r > 0.6 && free(-W / 2 + 1.2, D / 2 + 0.5, 1.2)) {
      const cols: RGB[] = [
        [0.45, 0.12, 0.08],
        [0.15, 0.25, 0.38],
        [0.32, 0.32, 0.22],
      ];
      for (let i = 0; i < 2; i++) F.cyl(props, -W / 2 + 0.9 + i * 0.62, D / 2 + 0.45, 0, 0.88, 0.29, 0.29, 8, cols[(id + i) % 3]!);
    }
    for (let x = -W / 2 + 0.6; x < W / 2 - 0.6; x += 1.6) {
      if (hash01(id, Math.round(x * 10), 331) < 0.45) {
        weed(fine, F.p(x, 0, D / 2 + 0.12), hash01(id, Math.round(x * 10), 332));
        stats.weeds++;
      }
    }
  }
}

/** A tuft of weeds / willowherb: three crossed quads, varied greens (purple flower spikes on some). */
function weed(b: MeshBuilder, p: P3, h: number): void {
  // knee-high at most and dark: taller pale tufts read as paper shards from the gameplay camera
  const s = 0.28 + h * 0.45;
  const col: RGB = h < 0.3 ? [0.03, 0.05, 0.015] : h < 0.7 ? [0.04, 0.06, 0.02] : [0.05, 0.05, 0.025];
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI + h * 2;
    const dx = Math.cos(a) * s * 0.5;
    const dz = Math.sin(a) * s * 0.5;
    b.quad([p[0] - dx, p[1], p[2] - dz], [p[0] + dx, p[1], p[2] + dz], [p[0] + dx * 0.6, p[1] + s, p[2] + dz * 0.6], [p[0] - dx * 0.6, p[1] + s, p[2] - dz * 0.6], WHITE, col);
    b.quad([p[0] + dx, p[1], p[2] + dz], [p[0] - dx, p[1], p[2] - dz], [p[0] - dx * 0.6, p[1] + s, p[2] - dz * 0.6], [p[0] + dx * 0.6, p[1] + s, p[2] + dz * 0.6], WHITE, col);
  }
  if (h > 0.82) b.cylinder(p[0], p[2], p[1] + s * 0.6, p[1] + s * 1.5, 0.05, 0.02, 4, [0.45, 0.14, 0.32], WHITE, { top: false });
}

// ------------------------------------------------------------------------------------- back yards

function backYards(c: Ctx, stats: StreetLifeStats): void {
  const yardWalls = c.decor.walls.filter((w) => w.kind === 'brick');
  const hit = (o: V2, d: V2, w: WallDecor): number => {
    const ex = w.b.x - w.a.x;
    const ez = w.b.z - w.a.z;
    const den = d.x * ez - d.z * ex;
    if (Math.abs(den) < 1e-9) return Infinity;
    const t = ((w.a.x - o.x) * ez - (w.a.z - o.z) * ex) / den;
    const u = ((w.a.x - o.x) * d.z - (w.a.z - o.z) * d.x) / den;
    return t > 0 && u >= 0 && u <= 1 ? t : Infinity;
  };
  const sheets: RGB[] = [
    [0.86, 0.85, 0.8],
    [0.8, 0.82, 0.86],
    [0.75, 0.62, 0.62],
    [0.62, 0.7, 0.78],
    [0.85, 0.8, 0.6],
  ];
  for (const lot of c.layout.buildings) {
    if (lot.style !== 'terrace') continue;
    const W = lot.halfX * 2;
    const D = lot.halfZ * 2;
    const units = Math.max(1, Number(lot.tags.find((t) => t.startsWith('units:'))?.slice(6) ?? Math.round(W / 5.2)));
    const unitW = W / units;
    const F = new Frame(lot.center.x, lot.center.z, lot.angle);
    const back = { x: -Math.sin(lot.angle), z: -Math.cos(lot.angle) };
    const props = c.cb.get('props', lot.center.x, lot.center.z);
    const clutter = detail(c, 'clutter', lot.center.x, lot.center.z);
    const fine = detail(c, 'clutterFine', lot.center.x, lot.center.z);
    for (let u = 0; u < units; u++) {
      const x0 = -W / 2 + u * unitW;
      const doorLeft = hash01(lot.id, u, 7) < 0.5;
      // the scullery outrigger stands on the door side; the washing goes on the open side
      const openX = x0 + unitW * (doorLeft ? 0.28 : 0.72);
      const o3 = F.p(openX, 0, -D / 2);
      const o = { x: o3[0], z: o3[2] };
      let depth = 15;
      for (const w of yardWalls) depth = Math.min(depth, hit(o, back, w));
      if (depth > 14 || depth < 4.5) continue;
      const h = hash01(lot.id, u, 401);
      const lineEnd = -D / 2 - depth + 0.6;
      if (h < 0.55) {
        // washing line from a hook on the back wall to a prop pole by the yard wall (the forked pole model on
        // MEDIUM / HIGH, the plain post as its LOW stand-in)
        if (c.kit.washingPole) {
          const pp = F.p(openX, 0, lineEnd);
          emitStreetModel(c.cb.get('propsModel', lot.center.x, lot.center.z), c.kit.washingPole, place(pp[0], PAVE_Y, pp[2], F.h), { foot: 0.72, footHeight: 0.3 });
        }
        F.cyl(c.cb.get(c.kit.washingPole ? 'propsLow' : 'props', lot.center.x, lot.center.z), openX, lineEnd, 0, 2.3, 0.04, 0.03, 4, [0.35, 0.28, 0.2], false);
        const a = F.p(openX, 2.2, -D / 2 - 0.05);
        const b = F.p(openX, 2.25, lineEnd);
        props.beam(a, b, 0.018, [0.8, 0.8, 0.78], WHITE);
        const n = 2 + Math.floor(hash01(lot.id, u, 402) * 3);
        for (let k = 0; k < n; k++) {
          const t = (k + 0.7) / (n + 0.4);
          const lz = -D / 2 - 0.05 + (lineEnd + D / 2 + 0.05) * t;
          const w = 0.5 + hash01(lot.id, u, 403 + k) * 0.5;
          const hh = 0.45 + hash01(lot.id, u, 410 + k) * 0.5;
          const col = sheets[Math.floor(hash01(lot.id, u, 420 + k) * sheets.length)]!;
          // hung across the line (faces the side yards) — two faces, slightly sagging
          const top = 2.2 - 0.05 * Math.sin(t * Math.PI);
          const p0 = F.p(openX, top - hh, lz - w / 2);
          const p1 = F.p(openX, top - hh, lz + w / 2);
          const p2 = F.p(openX, top, lz + w / 2);
          const p3 = F.p(openX, top, lz - w / 2);
          props.quad(p0, p1, p2, p3, WHITE, col);
          props.quad(p1, p0, p3, p2, WHITE, col);
        }
        stats.washingLines++;
      }
      // dustbins by the back gate, a coal bunker against the wall
      if (h > 0.3) dustbin(clutter, F, openX + (doorLeft ? 0.6 : -0.6), lineEnd + 0.1, h > 0.9);
      if (h < 0.2 || h > 0.85) F.box(clutter, x0 + unitW * (doorLeft ? 0.85 : 0.15), 0, -D / 2 - depth + 0.8, 0.9, 0.7, 0.8, [0.2, 0.19, 0.18]);
      // weeds along the yard wall
      for (let k = 0; k < 2; k++) {
        if (hash01(lot.id, u, 430 + k) < 0.6) {
          weed(fine, F.p(x0 + unitW * (0.2 + 0.6 * k), 0, -D / 2 - depth + 0.25), hash01(lot.id, u, 440 + k));
          stats.weeds++;
        }
      }
      // the odd back-garden tree (elder, sycamore self-seeded by the wall)
      if (depth > 6.5 && hash01(lot.id, u, 450) < 0.1) {
        const tp = F.p(x0 + unitW * (doorLeft ? 0.75 : 0.25), 0, -D / 2 - depth + 1.5);
        yardTree(c, tp, hash01(lot.id, u, 451));
      }
    }
  }
}

const foliageGeo = new THREE.IcosahedronGeometry(1, 1);
const _m = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _s = new THREE.Vector3();
const _p = new THREE.Vector3();

function yardTree(c: Ctx, p: P3, h: number): void {
  const props = c.cb.get('props', p[0], p[2]);
  const fol = c.cb.get('foliage', p[0], p[2]);
  const s = 0.7 + h * 0.5;
  props.cylinder(p[0], p[2], p[1], p[1] + 2.6 * s, 0.14 * s, 0.1 * s, 6, [0.3, 0.27, 0.22], WHITE);
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2 + h * 5;
    const shade = 0.85 + hash01(i, Math.round(h * 1000)) * 0.3;
    _q.setFromAxisAngle(_p.set(0, 1, 0), a);
    _m.compose(_p.set(p[0] + Math.cos(a) * 1.0 * s, p[1] + (3.3 + (i % 2) * 0.6) * s, p[2] + Math.sin(a) * 1.0 * s), _q, _s.set(1.5 * s, 1.2 * s, 1.5 * s));
    fol.geometry(foliageGeo, _m, [0.09 * shade, 0.15 * shade, 0.06 * shade], WHITE);
  }
}

// ------------------------------------------------------------------------------------- posters & tags

function wallDressing(c: Ctx, stats: StreetLifeStats): void {
  const sb = c.cb.global('signs');
  const { nodes, edges } = c.layout.roads;
  const alleyDist = (p: V2): number => {
    let best = Infinity;
    for (const e of edges) {
      if (!e.alley) continue;
      const a = nodes[e.from]!.pos;
      const b = nodes[e.to]!.pos;
      const dx = b.x - a.x;
      const dz = b.z - a.z;
      const l2 = dx * dx + dz * dz;
      const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.z - a.z) * dz) / l2));
      best = Math.min(best, Math.hypot(p.x - a.x - dx * t, p.z - a.z - dz * t));
    }
    return best;
  };
  let pk = 0;
  let gk = 0;
  // Alley yard walls: posters and tags on the alley face, weeds at the foot.
  for (const w of c.decor.walls) {
    if (w.kind !== 'brick' && w.kind !== 'corrugated' && w.kind !== 'palisade') continue;
    const len = Math.hypot(w.b.x - w.a.x, w.b.z - w.a.z);
    if (len < 3) continue;
    const d = { x: (w.b.x - w.a.x) / len, z: (w.b.z - w.a.z) / len };
    const n = { x: -d.z, z: d.x };
    const mid = { x: (w.a.x + w.b.x) / 2, z: (w.a.z + w.b.z) / 2 };
    const sideA = { x: mid.x + n.x * 1.2, z: mid.z + n.z * 1.2 };
    const sideB = { x: mid.x - n.x * 1.2, z: mid.z - n.z * 1.2 };
    const s = w.kind === 'brick' ? (alleyDist(sideA) < alleyDist(sideB) ? 1 : -1) : insideLot(c, sideA, 0.2) ? -1 : 1;
    const th = w.kind === 'brick' ? 0.18 : 0.08;
    const off = { x: n.x * s * (th + 0.02), z: n.z * s * (th + 0.02) };
    const heading = Math.atan2(n.x * s, n.z * s);
    const fine = detail(c, 'clutterFine', mid.x, mid.z);
    for (let t = 1.5; t < len - 1.5; t += 4.5) {
      const h = hash01(Math.round(w.a.x * 10), Math.round(w.a.z * 10), Math.round(t * 10));
      const p = { x: w.a.x + d.x * t + off.x, z: w.a.z + d.z * t + off.z };
      const F = new Frame(p.x, p.z, heading);
      if (w.kind !== 'palisade' && h < 0.3 && w.height > 1.9) {
        const n2 = 1 + Math.floor(h * 10) % 3;
        for (let k = 0; k < n2; k++) {
          const sg = c.signs.add(POSTERS[pk++ % POSTERS.length]!, 'poster');
          const pw = 0.62;
          F.sign(sb, sg.uv, k * 0.66 - (n2 - 1) * 0.33, 0.55 + (k % 2) * 0.08, 0.005 * k, pw, pw / sg.aspect);
          stats.posters++;
        }
      } else if (w.kind !== 'palisade' && h < 0.5) {
        const sg = c.signs.add(TAGS[gk++ % TAGS.length]!, 'graffiti');
        const gw = 1.6 + (h - 0.3) * 4;
        F.sign(sb, sg.uv, 0, 0.35, 0, gw, gw / sg.aspect);
        stats.tags++;
      }
      if (h > 0.55) {
        weed(fine, [p.x + n.x * s * 0.15, PAVE_Y, p.z + n.z * s * 0.15], h);
        stats.weeds++;
      }
    }
  }
  // Gable ends without a billboard and lock-up doors.
  for (const lot of c.layout.buildings) {
    const F = new Frame(lot.center.x, lot.center.z, lot.angle);
    const h = hash01(lot.id, 501);
    if (lot.style === 'lockup' && h < 0.55) {
      const sg = c.signs.add(TAGS[gk++ % TAGS.length]!, 'graffiti');
      const x = (hash01(lot.id, 502) - 0.5) * (lot.halfX * 2 - 3);
      F.sign(sb, sg.uv, x, 0.4, lot.halfZ + 0.03, 2.2, 2.2 / sg.aspect);
      stats.tags++;
    }
    if ((lot.style === 'terrace' || lot.style === 'warehouse' || lot.style === 'shopfront') && !lot.tags.some((t) => t.startsWith('billboard:'))) {
      for (const [tag, sx] of [
        ['open-left', 1],
        ['open-right', -1],
      ] as const) {
        if (!lot.tags.includes(tag) || hash01(lot.id, sx > 0 ? 503 : 504) > 0.5) continue;
        // facing out of the side wall (local +X for open-left)
        const gp = F.p(sx * (lot.halfX + 0.03), 0, 0);
        const G = new Frame(gp[0], gp[2], lot.angle + (sx * Math.PI) / 2);
        const n2 = 2 + Math.floor(hash01(lot.id, 505) * 3);
        for (let k = 0; k < n2; k++) {
          const sg = c.signs.add(POSTERS[pk++ % POSTERS.length]!, 'poster');
          const pw = 0.66;
          G.sign(sb, sg.uv, -lot.halfZ + 1.2 + k * 0.7, 0.6 + (k % 2) * 0.12, 0.004 * k, pw, pw / sg.aspect);
          stats.posters++;
        }
        if (hash01(lot.id, 506) < 0.5) {
          const sg = c.signs.add(TAGS[gk++ % TAGS.length]!, 'graffiti');
          G.sign(sb, sg.uv, lot.halfZ - 2.2, 0.3, 0.01, 2.4, 2.4 / sg.aspect);
          stats.tags++;
        }
      }
    }
  }
  // Viaduct arches: tags on the brick between the lock-up doors.
  const V = c.decor.viaduct;
  for (const span of V.spans) {
    for (let z = span.z0 + 4; z < span.z1 - 4; z += 8.5) {
      const h = hash01(Math.round(z), 601);
      if (h > 0.3) continue;
      const sg = c.signs.add(TAGS[gk++ % TAGS.length]!, 'graffiti');
      const west = h < 0.18;
      const x = west ? V.x - V.halfWidth - 0.03 : V.x + V.halfWidth + 0.03;
      const F = new Frame(x, z + 4.25, west ? -Math.PI / 2 : Math.PI / 2);
      F.sign(sb, sg.uv, 0, 0.3, 0, 2.0, 2.0 / sg.aspect);
      stats.tags++;
    }
  }
}

// ------------------------------------------------------------------------------------- shop light

/** Warm light spilling from shop windows and pub glass onto the pavement (additive, scaled by the mood). */
function shopLight(c: Ctx): void {
  const b = c.cb.global('pools');
  const Y = PAVE_Y + 0.028;
  for (const lot of c.layout.buildings) {
    let col: RGB | null = null;
    switch (lot.style) {
      case 'shopfront':
        col = (lot.name ?? '').toLowerCase().includes('launderette') ? [0.16, 0.22, 0.22] : [0.2, 0.15, 0.08];
        break;
      case 'cafe':
        col = [0.24, 0.17, 0.09];
        break;
      case 'pub':
        col = [0.3, 0.18, 0.07];
        break;
      case 'club':
        col = [0.28, 0.1, 0.18];
        break;
      case 'station':
        col = [0.12, 0.16, 0.26];
        break;
      default:
        break;
    }
    if (!col) continue;
    const F = new Frame(lot.center.x, lot.center.z, lot.angle, Y);
    const W = lot.halfX * 2;
    const D = lot.halfZ * 2;
    const reach = 3.2;
    const P = (lx: number, lz: number): P3 => F.p(lx, 0, lz);
    // the quad straddles the frontage so only the outer half (the bright core included) lands on the pavement
    b.quad(P(-W / 2 - 0.5, D / 2 + reach), P(W / 2 + 0.5, D / 2 + reach), P(W / 2 + 0.5, D / 2 - reach * 0.6), P(-W / 2 - 0.5, D / 2 - reach * 0.6), [0, 0, 1, 1], col);
    if (lot.style === 'pub') {
      for (const side of [1, -1]) {
        if (!lot.tags.includes(side > 0 ? 'open-left' : 'open-right')) continue;
        const x = side * (W / 2);
        b.quadFacing(P(x - side * reach * 0.6, D / 2), P(x + side * reach, D / 2), P(x + side * reach, -D / 2), P(x - side * reach * 0.6, -D / 2), [0, 0, 1, 1], [col[0] * 0.7, col[1] * 0.7, col[2] * 0.7], [0, 1, 0]);
      }
    }
  }
  // phone boxes glow onto the pavement around them
  for (const p of c.decor.props) {
    if (p.type !== 'phoneBox') continue;
    const r = 2.2;
    b.quad([p.pos.x - r, Y, p.pos.z + r], [p.pos.x + r, Y, p.pos.z + r], [p.pos.x + r, Y, p.pos.z - r], [p.pos.x - r, Y, p.pos.z - r], [0, 0, 1, 1], [0.2, 0.18, 0.13]);
  }
}

// ------------------------------------------------------------------------------------- moored lighters

/** Dumb steel lighters and a tug moored in the canal and basin (behind the quay railings, so unreachable). */
function moored(c: Ctx): void {
  const canal = c.decor.water[0]!;
  const spots: { x: number; z: number; h: number; tug?: boolean }[] = [];
  for (const x of [-170, -62, 62, 132, 245]) spots.push({ x, z: canal.minZ + 2.4, h: Math.PI / 2 });
  for (const x of [-232, 30, 168]) spots.push({ x, z: canal.maxZ - 2.4, h: -Math.PI / 2 });
  spots.push({ x: 118, z: 226, h: Math.PI / 2 }, { x: 134, z: 226, h: Math.PI / 2 }, { x: 120, z: 238, h: Math.PI / 2, tug: true });
  for (const s of spots) {
    // keep clear of bridges and the viaduct
    if (c.decor.bridges.some((br) => {
      const e = c.layout.roads.edges[br.edgeId]!;
      return Math.abs(c.layout.roads.nodes[e.from]!.pos.x - s.x) < 16;
    }) || Math.abs(s.x - c.decor.viaduct.x) < 16) continue;
    const F = new Frame(s.x, s.z, s.h, WATER_Y);
    const len = 14 + hash01(Math.round(s.x), 7) * 6;
    // the boat models (MEDIUM / HIGH): a narrowboat on a third of the moorings, the lighter stretched to its
    // length elsewhere, both with the keel 0.4 m under the water; the boxes below become the LOW stand-in
    const boat = s.tug ? null : hash01(Math.round(s.x), 11) < 0.35 ? c.kit.narrowboat : c.kit.lighter;
    if (boat) {
      const mb = c.cb.get('propsModel', s.x, s.z);
      const narrow = boat === c.kit.narrowboat;
      emitStreetModel(mb, boat, place(s.x, WATER_Y - 0.4, s.z, s.h, 1, 1, narrow ? 1 : len / LIGHTER_LENGTH));
      if (!narrow) {
        // the cargo heaped in the open hold: coal, ballast / sand, or sheeted down (darker than the procedural
        // lighter's: an upward face at 0.42 is white in the day sun, and this one is most of the boat from above)
        const k = hash01(Math.round(s.x), 9);
        const cargo: RGB = k < 0.4 ? [0.04, 0.04, 0.045] : k < 0.7 ? [0.2, 0.17, 0.12] : [0.08, 0.13, 0.09];
        F.box(mb, 0, 0.85, 0, 3.2, k < 0.7 ? 0.5 : 0.7, len - 3.0, cargo);
        if (k < 0.7) for (let i = 0; i < 3; i++) F.box(mb, 0, 1.3, -len / 4 + (i * len) / 4, 2.0 - (i % 2) * 0.6, 0.25, 1.8, cargo);
      }
      // mooring ropes up to the nearest quay edge
      ropes(c, F, s, narrow ? 18 : len, mb);
    }
    const props = c.cb.get(boat ? 'propsLow' : 'props', s.x, s.z);
    if (s.tug) {
      const hull: RGB = [0.1, 0.1, 0.11];
      F.box(props, 0, -0.3, 0, 4.2, 1.6, 13, hull);
      F.box(props, 0, 1.3, 0, 3.9, 0.12, 12.6, [0.3, 0.24, 0.18]);
      F.box(props, 0, 1.3, -1.5, 2.8, 2.1, 4.2, [0.78, 0.74, 0.66]);
      F.box(props, 0, 3.4, -0.8, 2.4, 0.9, 2.4, [0.78, 0.74, 0.66]);
      F.cyl(props, 0, 1.4, 1.4, 5.2, 0.5, 0.45, 8, [0.62, 0.14, 0.1]);
      F.cyl(props, 0, 1.4, 4.6, 5.2, 0.52, 0.52, 8, [0.06, 0.06, 0.06]);
      for (const sx of [-2.15, 2.15]) F.box(props, sx, 0.6, 0, 0.3, 0.4, 11, [0.08, 0.07, 0.06]);
      continue;
    }
    const rust: RGB = [0.28 + hash01(Math.round(s.x), 8) * 0.12, 0.14, 0.09];
    // black hull with a rust-streaked rubbing band, raised hatch coamings round an open hold
    F.box(props, 0, -0.4, 0, 4.4, 1.5, len, [0.07, 0.07, 0.08]);
    F.box(props, 0, 0.75, 0, 4.5, 0.25, len + 0.1, rust);
    const coaming: RGB = [0.16, 0.12, 0.1];
    for (const sx of [-1.75, 1.75]) F.box(props, sx, 1.1, 0, 0.18, 0.45, len - 2.4, coaming);
    for (const lz of [-(len - 2.4) / 2, (len - 2.4) / 2]) F.box(props, 0, 1.1, lz, 3.5, 0.45, 0.18, coaming);
    // cargo: coal, ballast / sand, or sheeted down under a tarpaulin
    const k = hash01(Math.round(s.x), 9);
    const cargo: RGB = k < 0.4 ? [0.05, 0.05, 0.055] : k < 0.7 ? [0.42, 0.37, 0.28] : [0.11, 0.18, 0.13];
    F.box(props, 0, 1.1, 0, 3.3, k < 0.7 ? 0.3 : 0.5, len - 2.8, cargo);
    if (k < 0.7) for (let i = 0; i < 3; i++) F.box(props, 0, 1.4, -len / 4 + (i * len) / 4, 2.2 - (i % 2) * 0.6, 0.25, 2.0, cargo);
    ropes(c, F, s, len, props);
  }
  flotsam(c);
}

/** The lighter model's natural length (export_street_life.py): the game stretches it to each mooring's. */
const LIGHTER_LENGTH = 16;

/** Mooring ropes from a boat's ends up to the nearest quay edge. */
function ropes(c: Ctx, F: Frame, s: { x: number; z: number }, len: number, b: MeshBuilder): void {
  const canal = c.decor.water[0]!;
  const basin = c.decor.water[1]!;
  const quayZ = s.z > canal.maxZ + 10 ? basin.minZ - 0.4 : s.z < (canal.minZ + canal.maxZ) / 2 ? canal.minZ - 0.4 : canal.maxZ + 0.4;
  for (const lz of [-len / 2 + 1, len / 2 - 1]) {
    const a = F.p(0, 1.1, lz);
    b.beam(a, [a[0], PAVE_Y + 0.3, quayZ], 0.04, [0.45, 0.4, 0.3], WHITE);
  }
}

/**
 * What the tide leaves against the quay walls: a scum line of foam, weed and oil along both banks,
 * and flotsam drifting in it (planks, bottles, a crate lid, a tyre, a ring of orange buoy). HIGH and
 * MEDIUM (`clutter`), a few hundred triangles in all.
 */
function flotsam(c: Ctx): void {
  const canal = c.decor.water[0]!;
  const basin = c.decor.water[1]!;
  const y = WATER_Y + 0.02;
  const skip = (x: number): boolean =>
    c.decor.bridges.some((br) => {
      const e = c.layout.roads.edges[br.edgeId]!;
      return Math.abs(c.layout.roads.nodes[e.from]!.pos.x - x) < 9;
    }) || Math.abs(x - c.decor.viaduct.x) < 8;
  // dark olive (weed and oil, the odd fleck of foam), in short tapered drifts with gaps: a long even
  // pale strip read as a plank laid along the quay
  // (`props` is a lit standard material: an upward quad by day takes about 3.5x its linear vertex
  // colour before the sRGB curve, so 0.05 already reads mid-grey; to sit darker than the water the
  // scum is near black)
  const scumC: RGB = [0.016, 0.02, 0.009];
  const drift = (ax: number, az: number, along: [number, number], into: [number, number], h: number, k: number): void => {
    const len = 1.5 + h * 2.5;
    const w0 = 0.12 + hash01(k, 3, 95) * 0.35;
    const w1 = 0.08 + h * 0.25;
    const tone = 0.7 + h * 0.7;
    const col: RGB = h > 0.5 ? [scumC[0] * tone, scumC[1] * tone, scumC[2] * tone] : [0.034 * tone, 0.036 * tone, 0.026 * tone];
    const b = c.cb.get('clutter', ax, az);
    b.quadFacing(
      [ax, y, az],
      [ax + along[0] * len, y, az + along[1] * len],
      [ax + along[0] * len + into[0] * w1, y, az + along[1] * len + into[1] * w1],
      [ax + into[0] * w0, y, az + into[1] * w0],
      WHITE,
      col,
      [0, 1, 0],
    );
  };
  const line = (x0: number, x1: number, z: number, into: number): void => {
    for (let x = x0; x < x1; x += 4) {
      if (skip(x + 2)) continue;
      const h = hash01(Math.round(x), Math.round(z), 95);
      if (h > 0.6) continue;
      drift(x, z, [1, 0], [0, into], h, Math.round(x));
    }
  };
  line(-296, 296, canal.minZ + 0.35, 1);
  line(-296, 296, canal.maxZ - 0.35, -1);
  line(basin.minX + 1, 296, basin.minZ + 0.35, 1);
  for (let z = basin.minZ + 2; z < 296; z += 4) {
    const h = hash01(Math.round(z), 7, 95);
    if (h > 0.55) continue;
    drift(basin.minX + 0.35, z, [0, 1], [1, 0], h, Math.round(z));
  }
  for (let i = 0; i < 70; i++) {
    const h0 = hash01(i, 1, 96);
    const h1 = hash01(i, 2, 96);
    const h2 = hash01(i, 3, 96);
    const inBasin = h0 > 0.7;
    const x = inBasin ? basin.minX + 1.5 + h1 * 3 + (h2 < 0.5 ? 0 : h2 * 160) : -290 + h1 * 580;
    const z = inBasin ? basin.minZ + 1 + (h2 < 0.5 ? h2 * 150 : h2 * 3) : h2 < 0.5 ? canal.minZ + 0.8 + h2 * 3 : canal.maxZ - 0.8 - (h2 - 0.5) * 3;
    if (skip(x)) continue;
    const b = c.cb.get('clutter', x, z);
    const F = new Frame(x, z, h1 * 6.28, WATER_Y);
    const k = hash01(i, 4, 96);
    if (k < 0.4) F.box(b, 0, 0.0, 0, 0.18, 0.05, 1.0 + h2, [0.36, 0.3, 0.2]);
    else if (k < 0.6) F.cyl(b, 0, 0, 0.0, 0.08, 0.5, 0.5, 8, [0.06, 0.06, 0.06]);
    else if (k < 0.75) F.box(b, 0, 0.0, 0, 0.6, 0.05, 0.6, [0.42, 0.34, 0.22], tileUV(T.timber));
    else if (k < 0.85) F.cyl(b, 0, 0, 0.0, 0.3, 0.26, 0.26, 8, [0.6, 0.25, 0.05]);
    else F.cyl(b, 0, 0, 0.0, 0.1, 0.05, 0.05, 5, [0.3, 0.4, 0.28]);
  }
}

// ------------------------------------------------------------------------------------- puddles

export interface Puddles {
  readonly mesh: THREE.Mesh | null;
  /** Colour of the sky the water mirrors (mood `sky.mid`, linear). */
  setSky(c: [number, number, number]): void;
  /** 0 = dry (hidden), 1 = standing water after heavy rain. */
  setWetness(w: number): void;
  /** The alpha mask and ripple normal once the baked files are in (materials.ts `ready`). */
  setTextures(alpha: THREE.Texture, normal: THREE.Texture | null): void;
  dispose(): void;
}

/**
 * Standing water: one mesh, glossy dark material with a soft radial alpha. It mirrors the sky
 * environment and the lamp/shop light pools, and fades with the weather (hidden when dry, so it
 * costs no draw call in clear moods).
 */
export function createPuddles(builder: MeshBuilder, alphaTex: THREE.Texture, normal: THREE.Texture | null = null, envIntensity = 1.6): Puddles {
  const geo = builder.build();
  if (!geo) return { mesh: null, setSky() {}, setWetness() {}, setTextures() {}, dispose() {} };
  const mat = new THREE.MeshStandardMaterial({
    color: 0x0b0d10,
    roughness: 0.04,
    metalness: 0.0,
    envMapIntensity: envIntensity,
    alphaMap: alphaTex,
    // rain dimples the surface: a slow ripple so the mirrored sky and lamps wobble
    ...(normal ? { normalMap: normal, normalScale: new THREE.Vector2(0.06, 0.06) } : {}),
    transparent: true,
    opacity: 0,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -3,
    polygonOffsetUnits: -3,
  });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.name = 'puddles';
  mesh.renderOrder = 1;
  mesh.matrixAutoUpdate = false;
  mesh.receiveShadow = false;
  mesh.visible = false;
  return {
    mesh,
    setSky(c) {
      // the mirrored sky: a sheen that makes standing water read against wet tarmac at any hour
      mat.emissive.setRGB(c[0] * 0.3, c[1] * 0.32, c[2] * 0.36);
    },
    setWetness(w) {
      mat.opacity = 0.9 * w;
      mesh.visible = w > 0.02;
    },
    setTextures(alpha, n) {
      mat.alphaMap = alpha;
      if (mat.normalMap && n) mat.normalMap = n;
    },
    dispose() {
      geo.dispose();
      mat.dispose();
    },
  };
}
